import { supabase } from './supabase.js';

/**
 * Live preview of a coupon code at checkout. Calls the validate_coupon
 * RPC which re-checks every rule (active, dates, usage caps, per-user
 * caps, first-order-only) and returns the discount the customer WOULD
 * get if they placed the order right now.
 *
 * IMPORTANT: this is preview-only. The actual discount applied to the
 * order is recomputed server-side by orders_recompute_totals. A spoofed
 * preview can't override that.
 *
 * @param {string} code            user-typed code (case-insensitive, trimmed in DB)
 * @param {number} subtotal        current cart subtotal
 * @param {number|null} categorySubtotal  only when caller pre-computed for category coupons
 * @returns {{valid:boolean, kind:string|null, freeShipping:boolean,
 *           discount:number, reason:string}}
 */
export async function validateCoupon(code, subtotal, categorySubtotal = null) {
  const { data, error } = await supabase.rpc('validate_coupon', {
    p_code: code,
    p_subtotal: Number(subtotal) || 0,
    p_category_subtotal: categorySubtotal == null ? null : Number(categorySubtotal),
  });
  if (error) {
    return { valid: false, kind: null, freeShipping: false, discount: 0, reason: error.message };
  }
  // RPC returns a single row in an array.
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) {
    return { valid: false, kind: null, freeShipping: false, discount: 0, reason: 'No response' };
  }
  return {
    valid: !!row.valid,
    kind: row.kind || null,
    freeShipping: !!row.free_shipping,
    discount: Number(row.discount) || 0,
    reason: row.reason || '',
  };
}
